import { useTicketUpdate } from 'features/libra/ticket/TicketUpdateContext'
import { useGeckoNotifications } from 'features/notifications'
import { useRolodexEntries } from 'features/rolodex'
import first from 'lodash/first'
import isEqual from 'lodash/isEqual'
import {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  GeckoAssetModel,
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoEntry,
  GeckoInspection,
  GeckoUnit,
  RolodexEntryType,
} from 'types'

import {
  NiiReportConfig,
  NiiReportTicketProps,
  niiReportTicketPropsSchema,
  useSaveTicketNiiData,
  useTicketNiiData,
} from './niiReportApi'

function pairEntries<T extends GeckoEntry, F extends GeckoEntry>(
  tagTo: ReadonlyArray<T>,
  tagFrom: ReadonlyArray<F>,
  tagKey: RolodexEntryType
): ReadonlyArray<{ from: F; to: T }> {
  const toMap = new Map<string, T>(tagTo.map((t) => [t.id, t]))
  return tagFrom
    .map((f) => {
      const tagValue = f.tags[tagKey]
      const to = tagValue ? toMap.get(tagValue) : undefined
      return to ? { from: f, to } : undefined
    })
    .filter((e): e is { from: F; to: T } => e !== undefined)
}

function useNiiAssetModelData(): {
  isLoading: boolean
  unit: GeckoUnit | undefined
  siteName: string
  assetModel: GeckoAssetModel | undefined
  componentDatasets: ReadonlyArray<GeckoComponentDataFrame>
  inspectionComponentPairs: ReadonlyArray<{
    inspection: GeckoInspection
    component: GeckoComponent
  }>
} {
  const { ticketTags } = useTicketUpdate()
  const unitId = ticketTags.unit
  const unitQueryBody = useMemo(
    () =>
      unitId
        ? {
            tags: { unit: unitId },
            type: [RolodexEntryType.UNIT],
          }
        : undefined,
    [unitId]
  )
  const { units, isLoading: isLoadingUnit } = useRolodexEntries(unitQueryBody)
  const entriesQueryBody = useMemo(
    () =>
      unitId
        ? {
            tags: { unit: unitId },
            type: [
              RolodexEntryType.COMPONENT,
              RolodexEntryType.INSPECTION,
              RolodexEntryType.ASSET_MODEL,
              RolodexEntryType.COMPONENT_DATA_FRAME,
              RolodexEntryType.COMPONENT_DATASET,
            ],
          }
        : undefined,
    [unitId]
  )
  const {
    components,
    inspections,
    assetModels,
    componentDataFrames,
    isLoading,
  } = useRolodexEntries(entriesQueryBody)

  const sitesQueryBody = useMemo(
    () =>
      unitId
        ? {
            ids: units
              .map((unit) => unit.tags.site)
              .filter((site): site is string => site !== undefined),
            type: [RolodexEntryType.SITE],
          }
        : undefined,
    [unitId, units]
  )
  const { sites, isLoading: isLoadingSites } = useRolodexEntries(sitesQueryBody)

  return useMemo(() => {
    const unit = first(units)
    const site = first(sites)
    const assetModel = first(assetModels)
    const cdss = componentDataFrames
    const inspectionComponentPairs = pairEntries(
      components,
      inspections,
      RolodexEntryType.COMPONENT
    ).map(({ from, to }) => ({ inspection: from, component: to }))
    return {
      isLoading: isLoadingUnit || isLoading || isLoadingSites,
      unit,
      assetModel,
      siteName: site?.data.name ?? '',
      inspectionComponentPairs,
      componentDatasets: cdss,
    }
  }, [
    assetModels,
    componentDataFrames,
    components,
    inspections,
    isLoading,
    isLoadingSites,
    isLoadingUnit,
    sites,
    units,
  ])
}

interface NiiReportDataContextValue {
  assetModelData: Omit<ReturnType<typeof useNiiAssetModelData>, 'isLoading'>
  niiData: NiiReportTicketProps | undefined
  setNiiData: Dispatch<SetStateAction<NiiReportTicketProps | undefined>>
  niiConfig: NiiReportConfig
  hasChanges: boolean
  doSave: () => Promise<void>
  resetChanges: () => void
  isLoading: boolean
  isSaving: boolean
}

const defaultNiiData = niiReportTicketPropsSchema.parse({})

const NiiReportDataContext = createContext<NiiReportDataContextValue>({
  assetModelData: {
    unit: undefined,
    assetModel: undefined,
    siteName: '',
    inspectionComponentPairs: [],
    componentDatasets: [],
  },
  niiData: defaultNiiData,
  setNiiData: () => {},
  niiConfig: {},
  hasChanges: false,
  doSave: async () => {},
  resetChanges: () => {},
  isLoading: false,
  isSaving: false,
})

function useNiiData(): {
  niiData: NiiReportTicketProps | undefined
  setNiiData: Dispatch<SetStateAction<NiiReportTicketProps | undefined>>
  doSave: () => Promise<void>
  hasChanges: boolean
  resetChanges: () => void
  isLoading: boolean
  isSaving: boolean
} {
  const { isLoading: isLoadingTicketData, niiData: ticketNiiData } =
    useTicketNiiData()

  const [niiData, setNiiData] = useState<NiiReportTicketProps | undefined>(
    ticketNiiData
  )
  const [isSaving, setIsSaving] = useState(false)

  // Sync local state with ticket data
  useEffect(() => {
    setNiiData(ticketNiiData)
  }, [ticketNiiData])

  const hasChanges = useMemo(
    () => !isEqual(niiData, ticketNiiData),
    [niiData, ticketNiiData]
  )

  const { errorNotification } = useGeckoNotifications()
  const saveTicketNiiData = useSaveTicketNiiData()
  const doSave = useCallback(async () => {
    setIsSaving(true)
    try {
      if (niiData) {
        await saveTicketNiiData(niiData)
      } else {
        errorNotification('No NII data to save')
      }
    } finally {
      setIsSaving(false)
    }
  }, [errorNotification, niiData, saveTicketNiiData])

  const resetChanges = useCallback(() => {
    setNiiData(ticketNiiData)
  }, [ticketNiiData])

  return useMemo(
    () => ({
      niiData,
      setNiiData,
      doSave,
      hasChanges,
      resetChanges,
      isLoading: isLoadingTicketData || isSaving,
      isSaving,
    }),
    [
      niiData,
      setNiiData,
      doSave,
      hasChanges,
      resetChanges,
      isLoadingTicketData,
      isSaving,
    ]
  )
}

export const NiiReportDataProvider: FC<
  PropsWithChildren<{ niiConfig: NiiReportConfig }>
> = ({ children, niiConfig }) => {
  const assetModelDataRaw = useNiiAssetModelData()
  const assetModelData = useMemo(
    () => ({
      unit: assetModelDataRaw.unit,
      assetModel: assetModelDataRaw.assetModel,
      siteName: assetModelDataRaw.siteName,
      inspectionComponentPairs: assetModelDataRaw.inspectionComponentPairs,
      componentDatasets: assetModelDataRaw.componentDatasets,
    }),
    [
      assetModelDataRaw.unit,
      assetModelDataRaw.assetModel,
      assetModelDataRaw.siteName,
      assetModelDataRaw.inspectionComponentPairs,
      assetModelDataRaw.componentDatasets,
    ]
  )

  const {
    isLoading: isLoadingNiiData,
    isSaving,
    niiData,
    setNiiData,
    hasChanges,
    doSave,
    resetChanges,
  } = useNiiData()

  const isLoading = isLoadingNiiData || assetModelDataRaw.isLoading

  const value = useMemo(
    () => ({
      assetModelData,
      niiData,
      setNiiData,
      niiConfig,
      hasChanges,
      doSave,
      resetChanges,
      isSaving,
      isLoading,
    }),
    [
      assetModelData,
      niiData,
      setNiiData,
      niiConfig,
      hasChanges,
      doSave,
      resetChanges,
      isSaving,
      isLoading,
    ]
  )

  return (
    <NiiReportDataContext.Provider value={value}>
      {children}
    </NiiReportDataContext.Provider>
  )
}

export const useNiiReportData = () => {
  const context = useContext(NiiReportDataContext)
  if (!context) {
    throw new Error(
      'useNiiReportData must be used within NiiReportDataProvider'
    )
  }
  return context
}
